import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown, User, Handshake, Briefcase, ClipboardList, Globe2, ShieldCheck, Rocket, GraduationCap, FileText } from "lucide-react";
import { Button } from "./ui/button";
import LanguageSwitcher from "./LanguageSwitcher";
import { useLang } from "../i18n/LangContext";
import logoSTUDYGRI from "../assets/logo_studygri.png";

const PORTAL_LINKS = [
  { key: 'student', label: { ru: 'Студент', uk: 'Студент', en: 'Student', pl: 'Student', it: 'Studente', es: 'Estudiante', zh: '学生', ar: 'طالب' }, url: 'https://crm.studygri.com/client-login', Icon: User },
  { key: 'partner', label: { ru: 'Партнёр', uk: 'Партнер', en: 'Partner', pl: 'Partner', it: 'Partner', es: 'Socio', zh: '合作伙伴', ar: 'شريك' }, url: 'https://crm.studygri.com/partner-login', Icon: Handshake },
  { key: 'manager', label: { ru: 'Менеджер', uk: 'Менеджер', en: 'Manager', pl: 'Menedżer', it: 'Manager', es: 'Gerente', zh: '经理', ar: 'مدير' }, url: 'https://crm.studygri.com/auth', Icon: Briefcase },
];

const TESTS_LINKS = [
  { key: 'country-test', label: { ru: 'Тест на страну и программу', uk: 'Тест на країну та програму', en: 'Country & program test', pl: 'Test na kraj i program', it: 'Test paese e programma', es: 'Test país y programa', zh: '国家和项目测试', ar: 'اختبار البلد والبرنامج' }, url: '/test', Icon: Globe2 },
  { key: 'english-test', label: { ru: 'Тест на уровень английского', uk: 'Тест на рівень англійської', en: 'English level test', pl: 'Test poziomu angielskiego', it: 'Test livello di inglese', es: 'Test de nivel de inglés', zh: '英语水平测试', ar: 'اختبار مستوى الإنجليزية' }, url: '/english-test', Icon: ClipboardList },
];

const SERVICES_LINKS = [
  { key: 'pricing', label: { ru: 'Пакеты вступа и цены', uk: 'Пакети вступу та ціни', en: 'Admission packages & prices', pl: 'Pakiety rekrutacji i ceny', it: 'Pacchetti di ammissione e prezzi', es: 'Paquetes de admisión y precios', zh: '录取套餐和价格', ar: 'باقات القبول والأسعار' }, url: '/pricing', Icon: GraduationCap },
  { key: 'strategy', label: { ru: 'Стратегия вступа · €290', uk: 'Стратегія вступу · €290', en: 'Admission strategy · €290', pl: 'Strategia rekrutacji · €290', it: 'Strategia d\u0027ammissione · €290', es: 'Estrategia de admisión · €290', zh: '录取策略 · €290', ar: 'استراتيجية القبول · €290' }, url: '/strategy-form', Icon: FileText },
  { key: 'student-support', label: { ru: 'Сопровождение во время учёбы', uk: 'Супровід під час навчання', en: 'Support during studies', pl: 'Wsparcie podczas studiów', it: 'Supporto durante gli studi', es: 'Apoyo durante los estudios', zh: '学习期间的支持', ar: 'الدعم أثناء الدراسة' }, url: '/student-support', Icon: ShieldCheck },
  { key: 'career-track', label: { ru: 'Career Track — карьера', uk: 'Career Track — кар\u2019єра', en: 'Career Track', pl: 'Career Track — kariera', it: 'Career Track — carriera', es: 'Career Track — carrera', zh: 'Career Track 职业发展', ar: 'Career Track — المهنة' }, url: '/career-track', Icon: Rocket },
];

const Header = ({ onOpenForm }) => {
  const { t, lang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [portalOpen, setPortalOpen] = useState(false);
  const [testsOpen, setTestsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobilePortalOpen, setMobilePortalOpen] = useState(false);
  const [mobileTestsOpen, setMobileTestsOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const nav = [
    { label: t("nav.universities", "Вузы"), href: '/universities' },
    { label: t("nav.schools", "Школы-пансионы"), href: '/schools' },
    { label: t("nav.reviews", "Отзывы"), href: '/#reviews' },
    { label: t("nav.team", "Команда"), href: '/team' },
    { label: t("nav.webinar", "Вебинары"), href: '/webinar' },
    { label: t("nav.blog", "Блог"), href: '/blog' }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 bg-white ${scrolled ? 'shadow-md border-b border-[#E8E0D5]' : 'border-b border-[#E8E0D5]/40'}`}>
      <div className="max-w-[1440px] mx-auto px-5 md:px-8 h-24 md:h-32 flex items-center justify-between gap-4 md:gap-6">
        <Link to="/" className="flex items-center leading-none shrink-0 transition hover:opacity-85" aria-label="STUDYGRI — на главную" data-testid="header-logo">
          <img src={logoSTUDYGRI} alt="STUDYGRI" className="h-20 md:h-24 w-auto select-none" draggable={false} />
        </Link>

        <nav className="hidden min-[1400px]:flex items-center gap-4 2xl:gap-7 flex-1 justify-center min-w-0">
          {nav.map(n => (
            <Link key={n.href} to={n.href} className="text-[15px] font-medium leading-none whitespace-nowrap transition hover:text-[#C9A96E] inline-flex items-center" style={{ color: '#1B2A4A' }}>{n.label}</Link>
          ))}
          {/* Services dropdown */}
          <div className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)} data-testid="services-dropdown-wrap">
            <button className="text-[15px] font-medium leading-none whitespace-nowrap transition hover:text-[#C9A96E] inline-flex items-center gap-1" style={{ color: '#1B2A4A' }} data-testid="services-nav-btn">
              {t("nav.services", "Услуги")}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
            </button>
            {servicesOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-80 z-50" data-testid="services-dropdown">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden" style={{ border: '1px solid #E8E0D5' }}>
                  {SERVICES_LINKS.map(({ key, label, url, Icon }) => (
                    <Link key={key} to={url} className="flex items-center gap-3 px-4 py-3 text-sm font-semibold transition hover:bg-[#FAF8F5]" style={{ color: '#1B2A4A', borderBottom: '1px solid #F0EBE0' }} data-testid={`services-link-${key}`}>
                      <Icon className="w-4 h-4" style={{ color: '#C9A96E' }} />
                      {label[lang] || label.ru}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* Tests dropdown */}
          <div className="relative" onMouseEnter={() => setTestsOpen(true)} onMouseLeave={() => setTestsOpen(false)} data-testid="tests-dropdown-wrap">
            <button className="text-[15px] font-medium leading-none whitespace-nowrap transition hover:text-[#C9A96E] inline-flex items-center gap-1" style={{ color: '#1B2A4A' }} data-testid="tests-nav-btn">
              {t("nav.tests", "Тесты")}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${testsOpen ? 'rotate-180' : ''}`} />
            </button>
            {testsOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-72 z-50" data-testid="tests-dropdown">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden" style={{ border: '1px solid #E8E0D5' }}>
                  {TESTS_LINKS.map(({ key, label, url, Icon }) => (
                    <Link key={key} to={url} className="flex items-center gap-3 px-4 py-3 text-sm font-semibold transition hover:bg-[#FAF8F5]" style={{ color: '#1B2A4A', borderBottom: '1px solid #F0EBE0' }} data-testid={`tests-link-${key}`}>
                      <Icon className="w-4 h-4" style={{ color: '#C9A96E' }} />
                      {label[lang] || label.ru}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        <div className="hidden min-[1400px]:flex items-center gap-3 shrink-0">
          <LanguageSwitcher dark={false} />
          <div
            className="relative"
            onMouseEnter={() => setPortalOpen(true)}
            onMouseLeave={() => setPortalOpen(false)}
            data-testid="portal-dropdown-wrap"
          >
            <Button className="rounded-full px-4 h-10 font-semibold bg-white border-2 transition inline-flex items-center gap-1.5 whitespace-nowrap" style={{ borderColor: '#1B2A4A', color: '#1B2A4A' }} data-testid="portal-login-btn">
              {t("cta.signin", "Войти в портал")}
              <ChevronDown className={`w-4 h-4 transition-transform ${portalOpen ? 'rotate-180' : ''}`} />
            </Button>
            {portalOpen && (
              <div className="absolute right-0 top-full pt-2 w-60 z-50" data-testid="portal-dropdown">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden" style={{ border: '1px solid #E8E0D5' }}>
                  {PORTAL_LINKS.map(({ key, label, url, Icon }) => (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-3 text-sm font-semibold transition hover:bg-[#FAF8F5]"
                      style={{ color: '#1B2A4A', borderBottom: '1px solid #F0EBE0' }}
                      data-testid={`portal-link-${key}`}
                    >
                      <Icon className="w-4 h-4" style={{ color: '#C9A96E' }} />
                      {label[lang] || label.ru}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
          <Button onClick={onOpenForm} className="rounded-full px-5 h-10 font-semibold text-white transition hover:opacity-90 whitespace-nowrap" style={{ background: '#1B2A4A' }}>
            {t("cta.signup", "Записаться")}
          </Button>
        </div>

        <div className="flex min-[1400px]:hidden items-center gap-2 shrink-0">
          <button onClick={() => setOpen(true)} className="p-2" style={{ color: '#1B2A4A' }} aria-label="menu">
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 p-6 min-[1400px]:hidden overflow-y-auto" style={{ background: '#FAF8F5' }}>
          <div className="flex items-center justify-between mb-10">
            <Link to="/" onClick={() => setOpen(false)} className="flex items-center" data-testid="header-logo-mobile">
              <img src={logoSTUDYGRI} alt="STUDYGRI" className="h-16 w-auto" draggable={false} />
            </Link>
            <button onClick={() => setOpen(false)} className="p-2" style={{ color: '#1B2A4A' }}><X className="w-6 h-6" /></button>
          </div>
          <div className="flex flex-col gap-5">
            {nav.map(n => (
              <Link key={n.href} to={n.href} onClick={() => setOpen(false)} className="text-xl font-medium font-display" style={{ color: '#1B2A4A' }}>{n.label}</Link>
            ))}
            {/* Mobile Services dropdown */}
            <button onClick={() => setMobileServicesOpen(v => !v)} className="text-xl font-medium font-display inline-flex items-center gap-2" style={{ color: '#1B2A4A' }} data-testid="services-nav-btn-mobile">
              {t("nav.services", "Услуги")}
              <ChevronDown className={`w-4 h-4 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileServicesOpen && (
              <div className="ml-3 -mt-2 flex flex-col gap-3">
                {SERVICES_LINKS.map(({ key, label, url, Icon }) => (
                  <Link key={key} to={url} onClick={() => setOpen(false)} className="flex items-center gap-2 text-base" style={{ color: '#1B2A4A' }} data-testid={`services-link-mobile-${key}`}>
                    <Icon className="w-4 h-4" style={{ color: '#C9A96E' }} />
                    {label[lang] || label.ru}
                  </Link>
                ))}
              </div>
            )}
            {/* Mobile Tests dropdown */}
            <button onClick={() => setMobileTestsOpen(v => !v)} className="text-xl font-medium font-display inline-flex items-center gap-2" style={{ color: '#1B2A4A' }} data-testid="tests-nav-btn-mobile">
              {t("nav.tests", "Тесты")}
              <ChevronDown className={`w-4 h-4 transition-transform ${mobileTestsOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileTestsOpen && (
              <div className="ml-3 -mt-2 flex flex-col gap-3">
                {TESTS_LINKS.map(({ key, label, url, Icon }) => (
                  <Link key={key} to={url} onClick={() => setOpen(false)} className="flex items-center gap-2 text-base" style={{ color: '#1B2A4A' }} data-testid={`tests-link-mobile-${key}`}>
                    <Icon className="w-4 h-4" style={{ color: '#C9A96E' }} />
                    {label[lang] || label.ru}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <div className="mt-10 pt-6 border-t border-[#E8E0D5] flex flex-col gap-3">
            <LanguageSwitcher dark={false} />
            <Button onClick={() => setMobilePortalOpen(v => !v)} className="rounded-full h-12 font-semibold bg-white border-2 inline-flex items-center justify-center gap-1.5" style={{ borderColor: '#1B2A4A', color: '#1B2A4A' }} data-testid="portal-login-btn-mobile">
              {t("cta.signin", "Войти в портал")}
              <ChevronDown className={`w-4 h-4 transition-transform ${mobilePortalOpen ? 'rotate-180' : ''}`} />
            </Button>
            {mobilePortalOpen && (
              <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid #E8E0D5', background: '#fff' }}>
                {PORTAL_LINKS.map(({ key, label, url, Icon }) => (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-base font-semibold transition active:bg-[#FAF8F5]"
                    style={{ color: '#1B2A4A', borderBottom: '1px solid #F0EBE0' }}
                    data-testid={`portal-link-mobile-${key}`}
                  >
                    <Icon className="w-4 h-4" style={{ color: '#C9A96E' }} />
                    {label[lang] || label.ru}
                  </a>
                ))}
              </div>
            )}
            <Button onClick={() => { setOpen(false); onOpenForm(); }} className="rounded-full h-12 font-semibold text-white" style={{ background: '#1B2A4A' }}>{t("cta.signup", "Записаться")}</Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
